import { contentSeriesTagColors, Course } from '@/client/courses';
import {
  browseCardBox,
  StyledNewLabel,
} from '@/components/courses/courses.styles';
import { DateFormats } from '@/system-settings/enums';
import { AppChip } from '@/ui/chip';
import { formatDate } from '@/utils/date/date.helper';
import {
  CoursePlayIcon,
  isNewCourse,
  isUpcomingCourse,
  ShieldPlaceholderIcon,
} from '@/utils/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ANIMATED_THUMBNAIL_EXTENSION = '.gif';

const isAnimatedThumbnail = (url?: string) => {
  if (!url) return false;

  const [path] = url.split('?');

  return path.toLowerCase().endsWith(ANIMATED_THUMBNAIL_EXTENSION);
};

const StyledPlayButton = styled.span`
  position: absolute;
  top: 46px;
  right: 49px;
  transform: translate(50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: color-mix(in srgb, var(--blue-dark) 60%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: var(--blue-dark);
  }

  svg {
    position: relative;

    path {
      fill: var(--white-main);
    }
  }
`;

const StyledCard = styled.div`
  ${browseCardBox}
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--white-main);
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    background-color: var(--beige-main);

    ${StyledPlayButton} {
      opacity: 1;
    }
  }
`;

const StyledThumbnail = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--blue-opacity);
`;

const StyledThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
  padding: var(--default-padding-small);
  min-width: 0;
`;

const StyledBadges = styled.div`
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);
`;

const StyledTitle = styled.span`
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);
  font-weight: 600;
  color: var(--black-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export type BrowseViewCourseCardProps = {
  course: Course;
  onSelect: (courseId: string) => void;
};

export const BrowseViewCourseCard: React.FC<BrowseViewCourseCardProps> = ({
  course,
  onSelect,
}) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isInViewport, setIsInViewport] = useState(false);

  const thumbnailUrl = course?.courseThumbnail?.signedUrl;
  const isAnimated = isAnimatedThumbnail(thumbnailUrl);

  useEffect(() => {
    const card = cardRef.current;

    if (!isAnimated || !card) return;

    const observer = new IntersectionObserver(([entry]) =>
      setIsInViewport(entry.isIntersecting),
    );
    observer.observe(card);

    return () => observer.disconnect();
  }, [isAnimated]);

  const isUpcoming = isUpcomingCourse(course?.releaseDate);
  const isNew = isNewCourse(course?.releaseDate);
  const isCustom = !!course?.account;
  const hasBadges = isNew || isUpcoming || isCustom || !!course?.contentSeries;
  const activeThumbnailUrl =
    isAnimated && !isInViewport ? undefined : thumbnailUrl;

  const handleSelect = () => onSelect(course?.id);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleSelect();
  };

  return (
    <StyledCard
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <StyledThumbnail>
        {activeThumbnailUrl && (
          <StyledThumbnailImage
            src={activeThumbnailUrl}
            alt=""
            loading="lazy"
          />
        )}
        {!thumbnailUrl && <ShieldPlaceholderIcon />}
        <StyledPlayButton>
          <CoursePlayIcon />
        </StyledPlayButton>
      </StyledThumbnail>
      <StyledDetails>
        {hasBadges && (
          <StyledBadges>
            {isNew && <StyledNewLabel>{t('generic.new')}</StyledNewLabel>}
            {course?.contentSeries && (
              <AppChip
                label={t(`content.series.${course?.contentSeries}`)}
                type={contentSeriesTagColors[course?.contentSeries]}
                size="s"
              />
            )}
            {isUpcoming && (
              <AppChip
                label={t('content.upcoming')}
                type="secondary"
                size="s"
                tooltip={formatDate(course?.releaseDate, DateFormats.DATEONLY)}
              />
            )}
            {isCustom && (
              <AppChip label={t('generic.custom')} type="red" size="s" />
            )}
          </StyledBadges>
        )}
        <StyledTitle>{course?.name}</StyledTitle>
      </StyledDetails>
    </StyledCard>
  );
};
